import {
	Drawer,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import { mapSearchResults } from "./adapter";
import CloseIcon from "./components/icons/close";
import FilterIcon from "./components/icons/filter";
import search from "./search";
import { CategoryListItemType } from "./types";

const MobileProductFinder = ({ list }: { list: CategoryListItemType[] }) => {
	const theme = useTheme();
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [selectedCategory, setSelectedCategory] = useState(0);
	const [loading, setLoading] = useState(false);
	const [results, setResults] = useState<{
		headers: string[];
		rows: React.JSX.Element[][];
	}>({ headers: [], rows: [] });
	const [selectedFilters, setSelectedFilters] = useState<{
		[k in string]: string;
	}>({});
	const [canResetFilters, setCanResetFilters] = useState(false);
	const resetFilters = () => {
		setSelectedFilters({});
		setCanResetFilters(false);
	};

	const handleFilterChange = (filter: string, value: string) => {
		if (value === "") {
			const { [filter]: _, ...rest } = selectedFilters;
			setSelectedFilters(rest);
			if (Object.keys(rest).length === 0) setCanResetFilters(false);
			else setCanResetFilters(true);
			return;
		}
		setSelectedFilters({ ...selectedFilters, [filter]: value });
		setCanResetFilters(true);
	};

	const showSearchResults = async () => {
		setLoading(true);
		const searchResults = await search(
			list[selectedCategory].label,
			Object.keys(selectedFilters).map(
				(key) =>
					`${key.split(" ").join("_").toLowerCase()}='${
						selectedFilters[key]
					}'`
			)
		);
		if (searchResults) {
			const mappedResults = mapSearchResults(
				list[selectedCategory].label,
				searchResults
			);
			if (mappedResults) setResults(mappedResults);
		}
		setLoading(false);
	};

	useEffect(() => {
		setSelectedFilters({});
		setCanResetFilters(false);
		showSearchResults();
	}, [selectedCategory]);

	useEffect(() => {
		showSearchResults();
	}, [selectedFilters]);
	return (
		<div className="p-5">
			<div className="flex gap-5">
				<Select
					fullWidth
					variant="outlined"
					SelectDisplayProps={{
						style: {
							padding: "5px 8px",
							paddingRight: "30px",
							fontSize: "12px",
							lineHeight: "14px",
						},
					}}
					value={selectedCategory}
					onChange={(e) =>
						setSelectedCategory(e.target.value as number)
					}
				>
					{list.map((item, index) => (
						<MenuItem
							key={index}
							value={index}
							sx={{ fontSize: "12px", lineHeight: "14px" }}
						>
							{item.label}
						</MenuItem>
					))}
				</Select>
				<div
					onClick={() => setDrawerOpen(true)}
					className="min-w-9 min-h-9 max-h-9 max-w-9 flex justify-center items-center rounded-[4px] cursor-pointer bg-teradyne-blue"
				>
					<FilterIcon />
				</div>
			</div>
			{canResetFilters && (
				<div className="mt-3 flex justify-end">
					<button
						className="text-sm underline hover:no-underline hover:text-teradyne-blue focus:text-teradyne-blue"
						onClick={resetFilters}
					>
						Reset Filters
					</button>
				</div>
			)}
			<Drawer
				anchor="right"
				open={drawerOpen}
				onClose={() => setDrawerOpen(false)}
			>
				<div className="p-5 flex flex-col gap-5">
					<div className="flex gap-4 items-center">
						<div
							onClick={() => setDrawerOpen(false)}
							className="min-w-9 min-h-9 max-h-9 max-w-9 flex justify-center items-center rounded-[4px] cursor-pointer bg-teradyne-blue"
						>
							<CloseIcon />
						</div>
						<p className="font-semibold text-sm min-w-[210px] max-w-[210px]">
							{list[selectedCategory].label}
						</p>
					</div>
					{list[selectedCategory].filters?.map((filter, index) => (
						<FormControl sx={{ minWidth: 200 }} key={index}>
							<InputLabel
								id={`select-${filter.label
									.toLowerCase()
									.replace(/ /g, "-")}-label`}
								size="small"
								sx={{
									fontSize: "12px",
									lineHeight: "14px",
									transform: "translate(10px, 11px) scale(1)",
									"&.MuiInputLabel-shrink": {
										transform:
											"translate(14px, -5px) scale(0.75)",
									},
								}}
							>
								{filter.label}
							</InputLabel>
							<Select
								labelId={`select-${filter.label
									.toLowerCase()
									.replace(/ /g, "-")}-label`}
								id={`select-${filter.label
									.toLowerCase()
									.replace(/ /g, "-")}`}
								color="secondary"
								sx={{
									background: theme.palette.secondary.light,
									borderRadius: "6px",
								}}
								label={filter.label}
								SelectDisplayProps={{
									style: {
										padding: "10px 11px",
										paddingRight: "30px",
										fontSize: "12px",
										lineHeight: "14px",
										minHeight: "0",
									},
								}}
								value={selectedFilters[filter.label] || ""}
								onChange={(e) => {
									handleFilterChange(
										filter.label,
										e.target.value as string
									);
								}}
								MenuProps={{
									sx: {
										"& .MuiPopover-paper": {
											background:
												theme.palette.secondary.light,
										},
									},
								}}
							>
								<MenuItem
									value=""
									sx={{
										fontSize: "12px",
										lineHeight: "14px",
									}}
								>
									All
								</MenuItem>
								{filter.options.map((option, index) => (
									<MenuItem
										key={index}
										value={option.value}
										sx={{
											fontSize: "12px",
											lineHeight: "14px",
										}}
									>
										{option.label}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					))}
				</div>
			</Drawer>
			<div className="pt-5 flex flex-col gap-5">
				{!loading &&
					results.rows.map((row, index) => (
						<div
							className="p-5 flex flex-col gap-5 rounded-[6px] overflow-hidden"
							style={{
								backgroundColor: theme.palette.secondary.light,
							}}
						>
							{results.headers.map((header, index) => (
								<div key={index}>
									<div className="font-bold text-sm leading-4 pb-[10px] border-b border-[#B2B4B2] text-[#545859]">
										{header}
									</div>
									{row[index]}
								</div>
							))}
						</div>
					))}
				{!loading && results.rows.length === 0 && (
					<div className="p-5 flex flex-col gap-5 rounded-[6px] overflow-hidden">
						<div className="font-bold text-sm leading-4 pb-[10px] border-b border-[#B2B4B2] text-[#545859]">
							No results found
						</div>
					</div>
				)}
				{loading && (
					<div className="p-5 flex flex-col gap-5 rounded-[6px] overflow-hidden">
						<div className="font-bold text-sm leading-4 pb-[10px] border-b border-[#B2B4B2] text-[#545859]">
							Loading...
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default MobileProductFinder;
